import { MiddlewarePaymentError } from '../../error/index.js';
import type { InteractionPreCancelMiddlewareFn } from '../../flow.js';
import { type InteractionSuccessProps, type OnInteraction } from '../../flow/types.js';
import { logger } from '../../utils/index.js';

type CancelConfirmationInteractionProps = InteractionSuccessProps & {
  count: number;
};

/**
 * Middleware for handling cancel confirmation interactions.
 *
 * @param onInteraction Callback function to handle the interaction when a cancel confirmation is received
 * @param callbackOnConfirm Optional callback function to execute when the cancel action is confirmed by the user
 * @returns Middleware function to be used in the cancel confirmation flow
 */
export function cancelConfirmMw(
  onInteraction: OnInteraction<CancelConfirmationInteractionProps>,
  callbackOnConfirm?: () => Promise<void>
): ReturnType<InteractionPreCancelMiddlewareFn> {
  const fn: ReturnType<InteractionPreCancelMiddlewareFn> =
    ({ count }) =>
    async (next) => {
      try {
        const countValue = count();
        await onInteraction({ count: countValue });
        if (callbackOnConfirm) {
          await callbackOnConfirm();
        }
        next();
      } catch (error) {
        if (error instanceof Error) {
          logger.debug('Cancel confirmation aborted', { message: error.message, error });
        }
        const mwError = new MiddlewarePaymentError({ clientCode: 'CANCEL_ABORTED' });
        next(mwError);
      }
    };
  return fn;
}
